import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";
import {
  ErrorBoundary,
  type ErrorBoundaryFallback,
} from "@datadog/browser-rum-react";

import { CI_COMMIT_SHORT_SHA } from "utils/ctx";
import { getEnvironment } from "utils/environment";

// eslint-disable-next-line functional/no-let
let initialized = false;

/*
 * Email link scanners report errors from their embedded browser that are not
 * produced by our code. See #28094
 */
const CRAWLER_ERROR_PATTERN =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/u;

const isCrawlerNoise = (message: string | undefined): boolean =>
  message !== undefined && CRAWLER_ERROR_PATTERN.test(message);

/*
 * Browser extensions inject into the page's main world, so their uncaught
 * exceptions reach our global handler and RUM attributes them to the page. An
 * error is only discarded when no frame belongs to the application origin, so
 * one of ours that an extension merely triggered is still reported. See #28274
 */
const EXTENSION_FRAME_MARKER = "-extension://";

const isExtensionError = (
  stack: string | undefined,
  origin: string = window.location.origin,
): boolean =>
  stack !== undefined &&
  stack.includes(EXTENSION_FRAME_MARKER) &&
  !stack.includes(origin);

/*
 * Report-only CSP violations block nothing and only add noise, while enforced
 * ones are real blocked resources
 */
const isReportOnlyCsp = (
  source: string | undefined,
  disposition: string | undefined,
): boolean => source === "report" && disposition === "report";

/*
 * The Stability SLO needs views without application errors over total views.
 * A RUM view event only carries a total error count with no per-source
 * breakdown, so the view is marked here instead. See #28280
 */
const APP_ERROR_SOURCES = new Set(["custom", "source"]);
const ABORT_ERROR_MESSAGE = "The user aborted a request.";
const VIEW_APP_ERROR_KEY = "hasAppError";

const isAppError = (
  source: string | undefined,
  message: string | undefined,
): boolean =>
  source !== undefined &&
  APP_ERROR_SOURCES.has(source) &&
  !(message ?? "").includes(ABORT_ERROR_MESSAGE);

const initLogging = (): void => {
  if (initialized) {
    return;
  }
  initialized = true;

  if (getEnvironment() === "production") {
    const clientToken = "pubf832e176dde6e821f10999320637a8f7";
    const site = "datadoghq.com";
    datadogLogs.init({
      beforeSend: (log): boolean =>
        !isCrawlerNoise(log.message) && !isExtensionError(log.error?.stack),
      clientToken,
      env: getEnvironment(),
      forwardErrorsToLogs: true,
      service: "views",
      sessionSampleRate: 20,
      site,
      storeContextsAcrossPages: true,
      version: CI_COMMIT_SHORT_SHA,
    });

    datadogRum.init({
      allowedTracingUrls: [
        {
          match: "https://app.fluidattacks.com/api",
          propagatorTypes: ["tracecontext"],
        },
      ],
      applicationId: "2011bf57-3720-4196-a849-e5f967bc57b3",
      beforeSend: (event): boolean => {
        if (event.type !== "error") {
          return true;
        }
        if (
          isCrawlerNoise(event.error.message) ||
          isExtensionError(event.error.stack) ||
          isReportOnlyCsp(event.error.source, event.error.csp?.disposition)
        ) {
          return false;
        }
        if (isAppError(event.error.source, event.error.message)) {
          datadogRum.setViewContextProperty(VIEW_APP_ERROR_KEY, true);
        }

        return true;
      },
      clientToken,
      defaultPrivacyLevel: "mask-user-input",
      env: getEnvironment(),
      service: "views",
      sessionReplaySampleRate: 50,
      sessionSampleRate: 100,
      site,
      startSessionReplayRecordingManually: true,
      storeContextsAcrossPages: true,
      trackLongTasks: true,
      trackResources: true,
      trackUserInteractions: true,
      version: CI_COMMIT_SHORT_SHA,
    });
  }
};

const customFallback: ErrorBoundaryFallback = ({
  resetError,
  error,
}): JSX.Element => {
  return (
    <p>
      {"Oops, something went wrong! "}
      <strong>{String(error)}</strong>{" "}
      <button onClick={resetError}>{"Retry"}</button>
    </p>
  );
};

export {
  initLogging,
  isAppError,
  isCrawlerNoise,
  isExtensionError,
  isReportOnlyCsp,
  ErrorBoundary as CustomErrorBoundary,
  customFallback as CustomFallback,
};
